import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Slide,
  Button,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";

import HomeFooter from "../shared/HomeFooter";
import Navigation from "../shared/Navigation";

function Pricing() {
  React.useEffect(() => {
    document.title = "Pricing - ShieldShot";
    window.scrollTo(0, 0);
  }, []);

  const plans = [
    {
      name: "Starter",
      price: "USD 1.49",
      period: "/month",
      features: [
        "10 GB storage",
        "100 Photos/month",
        "50 Emails/month",
        "Basic steganography protection",
        "Limited email support"
      ],
      description: "Perfect for new creators with smaller userbases and workloads.",
      highlight: false,
      buttonText: "Get Started",
    },
    {
      name: "Basic",
      price: "USD 4.99",
      period: "/month",
      features: [
        "100 GB storage",
        "1,000 Photos & Videos/month",
        "500 Emails/month",
        "Basic video support",
        "Advanced steganography protection",
        "Basic tracking analytics",
        "Standard email support"
      ],
      description: "Ideal for small to mid scale creators with an established userbase.",
      highlight: true,
      buttonText: "Most Popular",
    },
    {
      name: "Plus",
      price: "USD 14.99",
      period: "/month",
      features: [
        "500 GB storage",
        "Unlimited photos and videos",
        "Unlimited email distribution",
        "Full video support",
        "Advanced steganography protection",
        "Advanced analytics dashboard",
        "Custom watermarks & branding",
        "Priority email support",
      ],
      description: "For large scale creators with high quantities of distributions.",
      highlight: false,
      buttonText: "Go Pro",
    },
    {
      name: "Premium",
      price: "USD 39.99",
      period: "/month",
      features: [
        "2 TB storage",
        "Unlimited photos and videos",
        "Unlimited email distribution",
        "Advanced video & photo steganography protection",
        "Personalized storefront",
        "Custom integrations",
        "API access",
        "SLA guarantee"
      ],
      description: "For enterprises with very large or unpredictable workloads.",
      highlight: false,
      buttonText: "Enterprise",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Navigation />
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #ffe259 0%, #ffa751 100%)",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                color: "#2d2d2d",
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              Affordable and Fair
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#2d2d2d",
                fontWeight: 400,
                maxWidth: 600,
                mx: "auto",
                fontSize: { xs: "1.2rem", md: "1.5rem" },
              }}
            >
              Choose a subscription that fits your needs. Or start with limited free access.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} lg={3} key={plan.name}>
              <Slide direction="up" in timeout={800 + index * 150}>
                <Paper
                  elevation={plan.highlight ? 12 : 4}
                  sx={{
                    borderRadius: 4,
                    textAlign: "center",
                    p: { xs: 3, md: 4 },
                    background: "white",
                    position: "relative",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transform: plan.highlight ? "scale(1.05)" : "scale(1)",
                    transition: "all 0.3s ease-in-out",
                    border: plan.highlight ? "3px solid #2997ff" : "1px solid transparent",
                    "&:hover": {
                      transform: plan.highlight ? "scale(1.08)" : "scale(1.03)",
                      boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {plan.highlight && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Chip
                        icon={<StarIcon />}
                        label="MOST POPULAR"
                        sx={{
                          background: "#2997ff",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                        }}
                        onClick={() => navigate("/login?signup=true")}
                      />
                    </Box>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: "#2997ff",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: { xs: "1.5rem", md: "1.75rem" },
                      }}
                    >
                      {plan.name}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", mb: 2 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          color: "#ff9800",
                          fontWeight: 700,
                          fontSize: { xs: "2rem", md: "2.5rem" },
                        }}
                      >
                        {plan.price}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "grey.600",
                          ml: 1,
                        }}
                      >
                        {plan.period}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "grey.600",
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        lineHeight: 1.4,
                        minHeight: 60,
                      }}
                    >
                      {plan.description}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, mb: 3 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <Box
                        key={featureIndex}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1.5,
                          justifyContent: "flex-start",
                        }}
                      >
                        <CheckIcon
                          sx={{
                            color: "#4caf50",
                            fontSize: "1.2rem",
                            mr: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#2d2d2d",
                            fontSize: { xs: "0.85rem", md: "0.9rem" },
                            textAlign: "left",
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant={plan.highlight ? "contained" : "outlined"}
                    fullWidth
                    sx={{
                      mt: "auto",
                      py: 1.5,
                      borderRadius: "25px",
                      fontWeight: 700,
                      fontSize: "1rem",
                      ...(plan.highlight
                        ? {
                            background: "#2997ff",
                            color: "white",
                            "&:hover": {
                              background: "#1976d2",
                            },
                          }
                        : {
                            color: "#2997ff",
                            borderColor: "#2997ff",
                            "&:hover": {
                              backgroundColor: "rgba(41, 151, 255, 0.1)",
                            },
                          }),
                    }}
                  >
                    {plan.buttonText}
                  </Button>
                </Paper>
              </Slide>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Section */}
        <Box sx={{ mt: { xs: 8, md: 12 }, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              color: "#2997ff",
              fontWeight: 700,
              mb: 4,
            }}
          >
            Frequently Asked Questions
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: "left", mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#2997ff", fontWeight: 600, mb: 1 }}>
                  Can I change plans anytime?
                </Typography>
                <Typography variant="body1" sx={{ color: "grey.700" }}>
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: "left", mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#2997ff", fontWeight: 600, mb: 1 }}>
                  Is there a free trial?
                </Typography>
                <Typography variant="body1" sx={{ color: "grey.700" }}>
                  We offer limited free access to get you started. No credit card required.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: "left", mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#2997ff", fontWeight: 600, mb: 1 }}>
                  What payment methods do you accept?
                </Typography>
                <Typography variant="body1" sx={{ color: "grey.700" }}>
                  We accept all major credit cards, PayPal, and bank transfers for enterprise plans.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: "left", mb: 3 }}>
                <Typography variant="h6" sx={{ color: "#2997ff", fontWeight: 600, mb: 1 }}>
                  Can I cancel anytime?
                </Typography>
                <Typography variant="body1" sx={{ color: "grey.700" }}>
                  Absolutely. No long-term contracts. Cancel anytime and keep access until your billing period ends.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ mt: 0, backgroundColor: "#f1f1f1", py: 0 }}>
        <HomeFooter />
      </Box>
    </Box>
  );
}

export default Pricing;
